<template>
    <div class="cb-wizard">
        <button class="cb-wizard__close"
            @click="close">&times;</button>
        <Step1 v-if="step == 1"
            :domains="domains"
            :urls="filteredUrls"
            :isDataLoading="isLoading"
            :url="url"
            :keyword="keyword"
            @keywordsFound="showStep2" />
        <Step2 v-else-if="step == 2"
            :keywordsList="keywords"
            @keywordsSelected="showStep3"
            @previous="showStep1" />
        <Step3 v-else-if="step == 3"
            :keywords="selectedKeywords"
            :url="url"
            :title="title"
            :description="description"
            @metadataSet="showStep4"
            @previous="showStep2" />
        <Step4 v-else-if="step == 4"
            :notes="notes"
            @strategyNotesSet="showStep5"
            @previous="showStep3" />
        <Step5 v-else-if="step = 5"
            @save="save"
            @previous="showStep4" />
        <div v-if="isSaving"
            class="cb-wizard__loader">
            <base-loader />
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Step1 from './step1.vue';
import Step2 from './step2.vue';
import Step3 from './step3.vue';
import Step4 from './step4.vue';
import Step5 from './step5.vue';
import BaseModalMixins from '@/common/BaseModalMixins'

export default {
    name: 'ContentBriefWizard',
    mixins: [BaseModalMixins],
    components: {
        Step1,
        Step2,
        Step3,
        Step4,
        Step5
    },
    data() {
        return {
            step: 1,
            keywords: [],
            url: '',
            keyword: '',
            selectedKeywords: [],
            title: '',
            description: '',
            notes: '',
            isLoading: false,
            isSaving: false,
        };
    },
    async created() {
        const EMPTY_URL_FILTERS = {
            brief_status: [],
            section: [],
            url_status: [],
            writing_status: [],
            page_types: [],
            url: null,
            is_optimized: null,
            sort_column: [],
            sort_order: [],
            page_from: 0,
            page_to: 100,
            date_from: null,
            date_to: null,
            url_attributes: [],
            fk_user_id_writer: [],
            fk_user_id_strategist: [],
            msv: {
                operator: null,
                value: null
            },
            keywords: {
                operator: null,
                value: null
            }
        };
        this.isLoading = true;
        await this.getUrlPlannerData({ clientId: this.clientId, filters: EMPTY_URL_FILTERS });
        this.isLoading = false;
    },
    computed: {
        ...mapState('rankZoneCommon', ['webProperties']),
        ...mapState('urlPlanning', ['urls']),
        domains() {
            return this.webProperties.filter(prop => prop.type === 'Clients').map(prop => prop.domain);
        },
        filteredUrls() {
            if (!this.urls) {
                return [];
            }
            return this.urls.map(item => item.url);
        }
    },
    methods: {
        ...mapActions('briefContent', ['createContentBrief']),
        ...mapActions('urlPlanning', ['getUrlPlannerData']),
        close() {
            this.$emit('close');
        },
        showStep1() {
            this.keywords = [];
            this.step = 1;
            this.url = '';
            this.keyword = '';
        },
        showStep2(data) {
            this.step = 2;
            this.isUrlInvalid = false;
            if (!data) {
                return;
            }
            const { keywords, url, keyword } = data;
            this.keywords = keywords;
            this.url = url;
            this.keyword = keyword;
        },
        showStep3(selectedKeywords) {
            this.step = 3;
            if (!selectedKeywords) {
                return;
            }

            this.selectedKeywords = selectedKeywords;
        },
        showStep4(data) {
            this.step = 4;
            if (!data) {
                return;
            }

            const { title, description } = data;
            this.title = title;
            this.description = description;
        },
        showStep5(notes) {
            this.notes = notes;
            this.step = 5;
        },
        async save() {
            const data = {
                clientId: this.clientId,
                keywords: this.selectedKeywords.map(({ msv, difficultyScore, keyword }) => ({
                    msv,
                    difficultyScore,
                    keyword
                })),
                url: this.url,
                titleTag: this.title,
                metaDesc: this.description,
                strategyNotes: this.notes
            }
            this.isSaving = true;
            const response = await this.createContentBrief(data);
            this.isSaving = false;
            if (Array.isArray(response)) {
                this.step = 1;
                return;
            }

            if (response.data?.content_brief_id) {
                this.$router.push({path: `/content-briefs/${response.data.content_brief_id}`})
            }
            this.close();
        }
    }
}
</script>

<style scoped
    lang="scss">
    .cb-wizard {
        position: relative;
        height: 95vh;

        &__close {
            position: absolute;
            top: 20px;
            right: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 15px;
            height: 15px;
            background: transparent;
            border: none;
            padding: 0;
            font-size: 30px;
            color: $rfx-lighter-blue;
            z-index: 10;
        }

        &__container {
            height: 100%;
        }

        &::v-deep .cb-wizard {
            &__container {
                display: flex;
            }

            &__header {
                text-align: center;
            }

            &__title {
                font-size: 24px;
                font-weight: bold;
                margin: 0 0 25px;
            }

            &__stepper {
                width: 554px;
                margin: 0 auto 50px;
            }

            &__body {
                flex: 1 1 fit-content;
                padding: 50px;
                display: flex;
                flex-direction: column;
            }

            &__sidebar {
                flex: 0 0 305px;
                background-color: $rfx-lightest-green;
                border-radius: 0 13px 13px 0;
                padding: 185px 40px 100px;
                box-sizing: border-box;
                position: relative;
                color: $rfx-medium-march;
                line-height: 20px;
            }

            &__sidebar-title,
            &__sidebar-pro-tip {
                font-weight: 700;
            }

            &__sidebar-pro-tip {
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                margin-top: 60px;
            }

            &__text {
                margin: 20px 0 0;
            }

            &__controls {
                position: absolute;
                left: 40px;
                bottom: 40px;
                display: flex;
                flex-direction: row-reverse;
                justify-content: flex-end;
                gap: 20px;
            }

            &__button {
                height: 40px;
                padding: 0 20px;
                border-radius: 4px;
                font-size: 10px;
                font-weight: 600;
                text-transform: uppercase;
                border: none;
                outline: none;
                cursor: pointer;
                background-color: $rfx-blue;
                color: #fff;
                display: block;

                &:disabled {
                    opacity: .4;
                    cursor: default !important;
                }

                &_inverted {
                    background: #fff;
                    color: $rfx-blue;
                    border: 1px solid $rfx-blue;
                }
            }
        }

        &__loader {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, .1);
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
</style>